// The safety card (spec §13.15): what the coach says when the athlete mentions
// pain, injury or illness. In the history it is the full card. Pinned at the
// top of the thread (threadView.js pinnedSafety: the newest one only, for 7
// days, until acknowledged) it is a slim one-line banner (PinnedSafety) that
// expands to the full card; a brand-new one opens expanded the first time it is
// seen on this device, then stays collapsed. "Talk to your coach" opens the
// composer about it. There is no Undo: nothing about the plan changed.
//
// Acknowledging is kept on this device only (provisional: there is no
// acknowledge endpoint yet).
import React, { useEffect, useState } from 'react';
import { HeartPulse } from 'lucide-react';
import { Button, FOCUS } from '../ds';
import { cn } from '../../lib/utils';
import { safetyPreview } from '../../v2/threadView';
import { CardActions, CardTitle, CoachCard, Eyebrow } from './coachCard';

const HEADLINE = 'Look after yourself first';

/**
 * `readOnly` (a coach viewing the athlete): no Talk to your coach, no Got it.
 * Only the pinned (unacknowledged) card is announced as an alert; the
 * history's older cards are a labelled note, not a dozen alerts (#69).
 * `onCollapse`: the pinned card's "Hide" back to the banner.
 */
export default function SafetyCard({ item, pinned = false, onTalk, onAcknowledge, onCollapse, readOnly = false }) {
  const b = item.body || {};
  const actions = b.actions || ['talk_to_coach'];
  const talk = !readOnly && actions.includes('talk_to_coach');
  const ack = !readOnly && pinned && onAcknowledge;
  return (
    <CoachCard tone="danger" role={pinned ? 'alert' : 'note'} aria-label={`From your coach: ${b.title || 'Safety'}`}>
      <Eyebrow tone="danger">{HEADLINE}</Eyebrow>
      {b.title && b.title !== HEADLINE && <CardTitle>{b.title}</CardTitle>}
      {b.text && <p className="m-0 type-body-small text-text-body">{b.text}</p>}
      {(talk || ack || onCollapse) && (
        <CardActions>
          {talk && <Button size="small" kind="secondary" onClick={() => onTalk?.(item)}>Talk to your coach</Button>}
          {ack && <Button size="small" onClick={() => onAcknowledge(item)}>Got it</Button>}
          {onCollapse && (
            <button type="button" onClick={onCollapse}
              className={cn('ml-auto min-h-[32px] rounded-full px-1 type-label text-text-secondary hover:text-text-primary', FOCUS)}>
              Hide
            </button>
          )}
        </CardActions>
      )}
    </CoachCard>
  );
}

// Per-device memory of the safety items already seen (expanded once).
const readSeen = (key) => {
  try { const v = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(v) ? v.map(String) : []; } catch { return []; }
};
const markSeen = (key, id) => {
  try {
    const seen = readSeen(key);
    if (!seen.includes(id)) localStorage.setItem(key, JSON.stringify([...seen, id].slice(-50)));
  } catch { /* private mode: it opens expanded again next visit */ }
};

/**
 * The pinned safety item: a one-line banner (icon, "Look after yourself first ·
 * <first words>", Read, Got it) that expands to the full card. `seenKey` is
 * the localStorage key of the ids already seen; `defaultExpanded` overrides
 * the first-view rule (tests, the preview).
 */
export function PinnedSafety({ item, seenKey, defaultExpanded, onTalk, onAcknowledge, readOnly = false }) {
  const id = String(item.id);
  const [expanded, setExpanded] = useState(() => defaultExpanded ?? (seenKey ? !readSeen(seenKey).includes(id) : false));
  useEffect(() => { if (seenKey) markSeen(seenKey, id); }, [seenKey, id]);
  if (expanded) {
    return <SafetyCard item={item} pinned onTalk={onTalk} onAcknowledge={onAcknowledge} onCollapse={() => setExpanded(false)} readOnly={readOnly} />;
  }
  const b = item.body || {};
  const preview = safetyPreview(b.text || b.title);
  const ack = !readOnly && onAcknowledge;
  return (
    <div role="alert" aria-label={`From your coach: ${b.title || 'Safety'}`}
      className="flex min-w-0 items-center gap-2 rounded-chip border border-status-danger bg-bg-surface py-1.5 pl-3 pr-1.5">
      <HeartPulse size={15} aria-hidden="true" className="shrink-0 text-status-danger" />
      <p className="m-0 min-w-0 flex-1 line-clamp-2 type-body-small text-text-body">
        <span className="font-semibold text-status-danger">{HEADLINE}</span>
        {preview && <span className="text-text-secondary"> · {preview}</span>}
      </p>
      <button type="button" onClick={() => setExpanded(true)} aria-expanded="false"
        className={cn('shrink-0 min-h-[32px] rounded-full px-2 type-label text-accent-default hover:text-accent-hover', FOCUS)}>
        Read
      </button>
      {ack && (
        <button type="button" onClick={() => onAcknowledge(item)}
          className={cn('shrink-0 min-h-[32px] rounded-full bg-accent-default px-3 type-label text-accent-on hover:bg-accent-hover', FOCUS)}>
          Got it
        </button>
      )}
    </div>
  );
}
